import type { FC, ReactNode, ComponentType } from 'react';
import classNames from 'classnames';
import { ArrowLeftIcon } from '@heroicons/react/outline';
import { Button } from 'components/atoms/Button';
import ReactMarkdown from 'react-markdown';
import SmartOnboardingProgressBar, {
  type ProgressSegment,
  type ProgressBarColor,
} from './SmartOnboardingProgressBar.component';

interface ActivationStepWrapperProps {
  children: ReactNode;
  className?: string;

  // Progress bar
  showProgressBar?: boolean;
  progressSegments?: ProgressSegment[];
  onProgressSegmentClick?: (segmentId: string) => void;
  progressBarColor?: ProgressBarColor;

  // Header content
  title?: string;
  subtitle?: string;
  icon?: ComponentType<{ className?: string }>;
  /** Appended to the default ReactMarkdown subtitle wrapper classes (per-step overrides). */
  subtitleMarkdownClassName?: string;

  // Back button
  showBackButton?: boolean;
  onBack?: () => void;
}

/**
 * Unified wrapper for onboarding / activation steps (banking signup, Chift connect, etc.)
 * Handles:
 * - Smart progress bar (optional, with automatic positioning)
 * - Title and subtitle
 * - Optional icon
 * - Back button
 * - Consistent padding and layout
 */
const OnboardingStepWrapper: FC<ActivationStepWrapperProps> = ({
  children,
  className = '',
  showProgressBar = true,
  progressSegments,
  onProgressSegmentClick,
  progressBarColor = 'bank',
  title,
  subtitle,
  icon: Icon,
  subtitleMarkdownClassName,
  showBackButton = false,
  onBack,
}) => {
  return (
    <div className={classNames('w-full max-w-2xl mx-auto', className)}>
      {/* Progress Bar with Back Button */}
      {showProgressBar && progressSegments && progressSegments.length > 0 && (
        <div className="relative mb-6">
          {/* Back Button - positioned absolutely on the left */}
          {showBackButton && onBack && (
            <Button
              asDefault
              className="absolute left-0 top-1/2 -translate-y-1/2 flex !items-center space-x-2 group"
              onClick={onBack}
            >
              <ArrowLeftIcon className="h-4 text-primary-500 group-hover:text-primary-700" />
            </Button>
          )}

          {/* Progress Bar - centered with more spacing */}
          <div className="mx-8">
            <SmartOnboardingProgressBar
              segments={progressSegments}
              onSegmentClick={onProgressSegmentClick}
              color={progressBarColor}
            />
          </div>
        </div>
      )}

      {/* Back Button (when no progress bar) */}
      {(!showProgressBar ||
        !progressSegments ||
        progressSegments.length === 0) &&
        showBackButton &&
        onBack && (
          <Button
            asDefault
            className="flex !items-center space-x-2 group mb-4"
            onClick={onBack}
          >
            <ArrowLeftIcon className="h-4 text-primary-500 group-hover:text-primary-700" />
          </Button>
        )}

      {/* Icon */}
      {Icon && (
        <div className="flex justify-center mb-4">
          <Icon className="w-12 h-12 text-bank" />
        </div>
      )}

      {/* Title and Subtitle */}
      {title && (
        <div className="mb-6">
          <h2 className="font-degular text-2xl font-bold text-center mb-2">
            {title}
          </h2>
          {subtitle ? (
            <div
              className={classNames(
                'font-inter text-base text-primary-600 text-center max-w-lg mx-auto [&_p]:text-base [&_p]:font-inter',
                subtitleMarkdownClassName,
              )}
            >
              <ReactMarkdown
                components={{
                  p: ({ children }) => (
                    <p className="font-inter text-base m-0">{children}</p>
                  ),
                  strong: ({ children }) => (
                    <strong className="underline">{children}</strong>
                  ),
                }}
              >
                {subtitle}
              </ReactMarkdown>
            </div>
          ) : null}
        </div>
      )}

      {/* Step Content */}
      {children}
    </div>
  );
};

export default OnboardingStepWrapper;
